import { amplitudeClientDev, amplitudeClientProduction } from '@/app'
import { Consents, SelectedStorageTier, UserDocument } from '@/api/types/user'
import { getCurrentUserInfo } from './auth'

const emailIsDevByDefault = (email: string) => {
  // renderro.com domain treat as development by default, before user is event created
  return email.endsWith('@renderro.com')
}

export const amplitudeApiKeyDev = 'c11b205f34cdac074b7f3af9d1b7e21f'
export const amplitudeApiKeyProduction = 'cfda023f98e0b8d4aa85f6476a64c3e9'
export const googleAnalyticsTrackingId = 'UA-163156852-1'

export const clientFromEmail = (email: string) => {
  return emailIsDevByDefault(email) ? amplitudeClientDev : amplitudeClientProduction
}

export const clientFromProfile = (profile: UserDocument) => {
  return profile && profile.environment === 'dev' ? amplitudeClientDev : amplitudeClientProduction
}

export const trackRegistrationAttempt = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(null)
  client.logEvent('Registration - Attempt', {
    type: 'email',
    email,
    account_type: 'individual',
  })
}

export const trackRegistrationFailure = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(null)
  client.logEvent('Registration - Failure', {
    type: 'email',
    email,
    account_type: 'individual',
  })
}

export const trackGoogleRegistrationFailure = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(null)
  client.logEvent('Registration - Failure', {
    type: 'google',
    email,
    account_type: 'individual',
  })
}

export const trackGoogleLoginAttempt = () => {
  const client = amplitudeClientProduction
  client.setUserId(null)
  client.logEvent('Login - Attempt', {
    type: 'google',
  })
}

export const trackGoogleLoginSuccess = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Login - Success', {
    type: 'google',
    email,
  })
}

export const trackGoogleLoginFailure = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Login - Failure', {
    type: 'google',
    email,
  })
}

export const trackLoginAttempt = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(null)
  client.logEvent('Login - Attempt', {
    type: 'email',
    email,
  })
}

export const trackLoginSuccess = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Login - Success', {
    type: 'email',
    email,
  })
}

export const trackLoginFailure = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(null)
  client.logEvent('Login - Failure', {
    type: 'email',
    email,
  })
}

export const trackLogout = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Logout', {
    email,
  })
}

export const trackCompleteProfile = (profile: UserDocument, consents: Consents) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Complete Profile - Consents Accepted', {
    data_processing: consents.data_processing,
    terms_and_conditions: consents.terms_and_conditions,
    terms_of_service: consents.terms_of_service,
    privacy_policy: consents.privacy_policy,
    email,
  })
}

export const trackPasswordResetAttempt = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Password Reset - Email Form - Attempt', {
    email,
  })
}

export const trackPasswordResetSuccess = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Password Reset - Email Form - Success', {
    email,
  })
}

export const trackPasswordResetFailure = (email: string) => {
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Password Reset - Email Form - Failure', {
    email,
  })
}

export const trackPaymentMethodUpdateAttempt = (profile: UserDocument) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Payment Method - Update - Attempt', {
    type: 'card',
    origin: 'settings',
    email,
  })
}

export const trackBillingUpdateAttempt = (profile: UserDocument, country: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Billing Information - Update - Attempt', {
    origin: 'settings',
    country,
    email,
  })
}

export const trackStoragePlanUpdateAttempt = (
  profile: UserDocument,
  codename: string,
  sizeInGbs?: number,
) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Storage Plan - Update - Attempt', {
    codename,
    size_in_gbs: sizeInGbs,
    email,
  })
}

export const trackDriveCreateAttempt = (profile: UserDocument, name: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Setup New - Attempt', {
    name,
    email,
  })
}

export const trackDriveSetupFinishedStep = (profile: UserDocument, stepName: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Setup New - Finished Step', {
    step_name: stepName,
    email,
  })
}

export const trackStoragePlanCustomization = (profile: UserDocument, codename: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Storage Plan - Customization - Attempt', {
    codename,
    email,
  })
}

export const trackStoragePlanCancelAttempt = (
  profile: UserDocument,
  selectedTier: SelectedStorageTier,
) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Storage Plan - Cancel - Attempt', {
    codename: selectedTier.codename,
    email,
  })
}

export const trackEditProfileAttempt = (profile: UserDocument, fields: string[]) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Account Settings - Edit Profile - Attempt', {
    fields,
    email,
  })
}

export const trackEditProfileSuccess = (profile: UserDocument, fields: string[]) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Account Settings - Edit Profile - Success', {
    fields,
  })
}

export const trackEditProfileFailure = (profile: UserDocument, fields: string[]) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Account Settings - Edit Profile - Failure', {
    fields,
    email,
  })
}

export const trackWorkstationSetupNewAttempt = (profile: UserDocument) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Setup New - Attempt', { email })
}

export const trackWorkstationSetupNewCancel = (profile: UserDocument) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Setup New - Cancel', {
    email,
  })
}

export const trackWorkstationSetupFinishedStep = (profile: UserDocument, stepName: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Setup New - Finished Step', {
    step_name: stepName,
    email,
  })
}

export const trackWorkstationSetupEditStep = (profile: UserDocument, stepName: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Setup New - Edit Step', {
    step_name: stepName,
    email,
  })
}

export const trackWorkstationStartAttempt = (profile: UserDocument, uuid: string, name: string) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Start - Attempt', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationStopAttempt = (profile: UserDocument, uuid: string, name: string) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Stop - Attempt', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationConnectRdpAttempt = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Connect RDP - Attempt', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationConnectRdpSuccess = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Connect RDP - Success', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationConnectRdpFailure = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Connect RDP - Failure', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationConnectTgxAttempt = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Connect TGX - Attempt', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationConnectTgxSuccess = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Connect TGX - Success', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationConnectTgxFailure = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Connect TGX - Failure', {
    email,
    uuid,
    name,
  })
}

export const trackWorkstationDeleteAttempt = (
  profile: UserDocument,
  uuid: string,
  name: string,
) => {
  // TODO add codenames here
  // we need to include them on the firebase collection
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Workstation - Delete - Attempt', {
    email,
    uuid,
    name,
  })
}

export const trackDriveBrowserOpen = (profile: UserDocument, uuid: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Browser - Open', {
    email,
    uuid,
  })
}

export const trackDriveBrowserViewExternalSoftwareDetails = (serverUrl: string) => {
  const email = getCurrentUserInfo().email
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Drive - Browser - View External Software Details', {
    email,
    server_url: serverUrl,
  })
}

export const trackDriveBrowserFilesUploadedAttempt = (profile: UserDocument, uuid: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Browser - Files Uploaded - Attempt', {
    email,
    uuid,
  })
}

export const trackDriveBrowserFilesDownloadedAttempt = (profile: UserDocument, uuid: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Browser - Files Downloaded - Attempt', {
    email,
    uuid,
  })
}

export const trackDriveBrowserRenameAttempt = (profile: UserDocument, uuid: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Browser - Rename - Attempt', {
    email,
    uuid,
  })
}

export const trackDriveBrowserDeleteAttempt = (profile: UserDocument, uuid: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Drive - Browser - Delete - Attempt', {
    email,
    uuid,
  })
}

export const trackBugReportAttempt = (
  profile: UserDocument,
  category: string,
  description: string,
) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Bug Report - Attempt', {
    email,
    description,
    category,
  })
}

export const trackCotactSupportAttempt = (
  profile: UserDocument,
  category: string,
  description: string,
) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Contact Support - Attempt', {
    email,
    description,
    category,
  })
}

export const trackPageView = (url: string) => {
  const email = getCurrentUserInfo().email
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Page View', {
    email,
    url,
  })
}

export const trackExternalPageView = (profile: UserDocument, url: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('External Page View', {
    email,
    url,
  })
}

export const trackMailLinkClick = (profile: UserDocument, emailTo: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Mail Link Click', {
    email,
    email_to: emailTo,
  })
}

export const trackVideoPlay = (profile: UserDocument, name: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Video - Play', {
    email,
    name,
  })
}

export const trackVideoPause = (profile: UserDocument, name: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Video - Pause', {
    email,
    name,
  })
}

export const trackVideoResume = (profile: UserDocument, name: string) => {
  const client = clientFromProfile(profile)
  const email = getCurrentUserInfo().email
  client.setUserId(email)
  client.logEvent('Video - Resume', {
    email,
    name,
  })
}

export const trackSwitchMainTab = (name: string) => {
  const email = getCurrentUserInfo().email
  const client = clientFromEmail(email)
  client.setUserId(email)
  client.logEvent('Switch Main Tab', {
    email,
    name,
  })
}
